import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileSpreadsheet,
  FileText,
  Film,
  Image as ImageIcon,
  Loader2,
  Trash2,
  Upload
} from 'lucide-react';
import toast from 'react-hot-toast';
import FormInput from '../../shared/components/ui/FormInput';
import Loader from '../../shared/components/ui/Loader';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import {
  createCampaignMediaLibrary,
  deleteCampaignMediaLibrary,
  getCampaignMediaLibrary,
  uploadFileToS3
} from '../services/api/implementation/impl';const FILE_TYPE_META = {
  pdf: { icon: FileText, bg: 'bg-red-100', text: 'text-red-600', label: 'PDF' },
  jpg: { icon: ImageIcon, bg: 'bg-blue-100', text: 'text-blue-600', label: 'JPG' },
  jpeg: { icon: ImageIcon, bg: 'bg-blue-100', text: 'text-blue-600', label: 'JPG' },
  png: { icon: ImageIcon, bg: 'bg-blue-100', text: 'text-blue-600', label: 'PNG' },
  xlsx: { icon: FileSpreadsheet, bg: 'bg-green-100', text: 'text-green-600', label: 'XLSX' },
  xls: { icon: FileSpreadsheet, bg: 'bg-green-100', text: 'text-green-600', label: 'XLS' },
  mp4: { icon: Film, bg: 'bg-orange-100', text: 'text-orange-600', label: 'MP4' }
};

const MIME_TO_EXT = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel': 'xls',
  'video/mp4': 'mp4'
};

const projectFormSchema = yup.object().shape({
  project: yup.string().required('Select Project')
});

function formatFileSize(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const rounded = unitIndex === 0 ? Math.round(size) : size.toFixed(1).replace(/\.0$/, '');
  return `${rounded} ${units[unitIndex]}`;
}

function formatAddedDate(iso) {
  if (!iso) return '—';
  try {
    const date = new Date(iso);
    return `Added ${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
  } catch {
    return '—';
  }
}

function getExtFromMediaItem(item) {
  const fromName = item?.name?.split('.').pop()?.toLowerCase();
  if (fromName && fromName.length <= 5) return fromName;
  return MIME_TO_EXT[item?.mime_type] || 'file';
}

function getFileMeta(ext) {
  return (
    FILE_TYPE_META[ext?.toLowerCase()] ?? {
      icon: FileText,
      bg: 'bg-gray-100',
      text: 'text-gray-600',
      label: (ext || 'FILE').toUpperCase()
    }
  );
}

function getUploadedFileUrl(result) {
  const fileData = result?.data?.files?.[0];
  return (
    fileData?.url ||
    fileData?.presignedUrl ||
    fileData?.Location ||
    result?.data?.url ||
    result?.Location ||
    null
  );
}

const WhatsAppMediaLibrary = () => {
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();
  const [itemToDelete, setItemToDelete] = useState(null);
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();  const {
    register,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(projectFormSchema),
    defaultValues: { project: '' }
  });

  const projectID = watch('project');

  useEffect(() => {
    if (projects?.length > 0 && !projectID) {
      setValue('project', projects[0].id);
    }
  }, [projects, projectID, setValue]);

  const {
    data: mediaItems = [],
    isLoading,
    isFetching
  } = useQuery({
    queryKey: ['campaignMediaLibrary', projectID],
    queryFn: () => getCampaignMediaLibrary(projectID),
    enabled: Boolean(projectID),
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || err?.message || 'Could not load media library.'
      );
    }
  });

  const { mutateAsync: uploadMedia, isPending: isUploading } = useMutation({
    mutationFn: async (file) => {
      const s3Result = await uploadFileToS3(file);
      const url = getUploadedFileUrl(s3Result);
      if (!url) {
        throw new Error(`Could not get URL for ${file.name}`);
      }
      await createCampaignMediaLibrary({
        project_id: Number(projectID),
        name: file.name,
        url,
        mime_type: file.type || 'application/octet-stream',
        file_size_bytes: file.size
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaignMediaLibrary', projectID] });
      toast.success('File uploaded successfully');
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to upload file.'
      );
    }
  });

  const { mutate: deleteMedia, isPending: isDeleting } = useMutation({
    mutationFn: (mediaId) => deleteCampaignMediaLibrary(mediaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaignMediaLibrary', projectID] });
      toast.success('File deleted successfully');
      setItemToDelete(null);
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to delete file.'
      );
    }
  });

  const handleUploadClick = () => {
    if (!projectID) {
      toast.error('Select a project first.');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const toastId = 'campaign-media-upload';
    toast.loading('Uploading file...', { id: toastId });
    try {
      await uploadMedia(file);
      toast.dismiss(toastId);
    } catch {
      toast.dismiss(toastId);
    }
  };

  return (
    <>
      <Loader
        isVisible={
          isProjectsLoading || (isLoading && Boolean(projectID)) || isUploading || isDeleting
        }
      />

      {itemToDelete ? (
        <Modal width={40} name="Delete media" onClose={() => setItemToDelete(null)}>
          <div className="flex flex-col items-center">
            <h2 className="text-center text-lg font-medium text-primary">
              Are you sure you want to delete this file?
            </h2>
            <p className="mt-3 text-center text-sm text-gray-600 break-all">
              {itemToDelete.name}
            </p>
            <div className="mt-8 flex w-full justify-end gap-3">
              <Button type="button" displayType="secondary" onClick={() => setItemToDelete(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                displayType="primary-red"
                disabled={isDeleting}
                isLoading={isDeleting}
                onClick={() => deleteMedia(itemToDelete.id)}>
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      <div className="pb-10">
        <div
          className="sticky z-[9] border-b border-stone-200/80 bg-white px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 shadow-sm"
          style={{ top: 'var(--wa-campaign-shell-height, 8.25rem)' }}>
          <div className="flex min-w-0 max-w-md flex-1 items-center">
            <FormInput
              width={100}
              label=""
              register={register}
              register_key="project"
              isRequired
              type="dropdown"
              options={[
                { label: 'Select Project', value: '', disabled: true },
                ...(projectsForDropdown || [])
              ]}
              errors={errors}
              hideAstrik
            />
          </div>

          <div className="flex items-center gap-3 sm:ml-auto shrink-0">
            {isFetching && !isLoading && !isUploading ? (
              <Loader2 className="h-4 w-4 animate-spin text-primary" aria-label="Refreshing" />
            ) : null}
            <button
              type="button"
              onClick={handleUploadClick}
              disabled={!projectID || isUploading}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 transition-colors disabled:cursor-not-allowed disabled:opacity-60">
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Upload
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileSelected}
            />
          </div>
        </div>

        <div className="px-6 py-6">
          <div className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden flex flex-col min-h-[calc(100vh-18rem)]">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">Media library</h2>
            </div>

            <div className="flex-1 overflow-auto">
              {!projectID ? (
                <div className="flex flex-col items-center justify-center py-20 text-center text-gray-500">
                  <p className="text-sm font-medium text-gray-700">Select a project</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Choose a project to view its campaign media files.
                  </p>
                </div>
              ) : mediaItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center text-gray-500">
                  <FileText className="h-10 w-10 text-gray-300 mb-3" />
                  <p className="text-sm font-medium text-gray-700">No media uploaded yet</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Uploaded files for this project will appear here.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {mediaItems.map((item) => {
                    const ext = getExtFromMediaItem(item);
                    const meta = getFileMeta(ext);
                    const Icon = meta.icon;

                    return (
                      <li key={item.id}>
                        <div className="flex w-full items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors">
                          {item.url ? (
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex min-w-0 flex-1 items-center gap-4">
                              <div
                                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${meta.bg}`}>
                                <Icon className={`h-5 w-5 ${meta.text}`} strokeWidth={2} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-semibold text-gray-900">
                                  {item.name}
                                </p>
                                <p className="mt-0.5 text-xs text-gray-500">
                                  {formatFileSize(item.file_size_bytes)} ·{' '}
                                  {formatAddedDate(item.created_at)}
                                </p>
                              </div>
                              <span
                                className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${meta.bg} ${meta.text}`}>
                                {meta.label}
                              </span>
                            </a>
                          ) : (
                            <div className="flex min-w-0 flex-1 items-center gap-4">
                              <div
                                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${meta.bg}`}>
                                <Icon className={`h-5 w-5 ${meta.text}`} strokeWidth={2} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-semibold text-gray-900">
                                  {item.name}
                                </p>
                                <p className="mt-0.5 text-xs text-gray-500">
                                  {formatFileSize(item.file_size_bytes)} ·{' '}
                                  {formatAddedDate(item.created_at)}
                                </p>
                              </div>
                              <span
                                className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${meta.bg} ${meta.text}`}>
                                {meta.label}
                              </span>
                            </div>
                          )}
                          <button
                            type="button"
                            title="Delete file"
                            onClick={() => setItemToDelete(item)}
                            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-400 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WhatsAppMediaLibrary;
